import React, { useState, useEffect, useMemo } from "react";
import "./VendorCare.css";
import { Link } from "react-router-dom";
import {
    updateTicketStatusAPI,
    getAllVendorTicketsAPI,
    getVendorTicketByIdAPI,
} from "../../../Servicefile/SupportManagementService.jsx";

const STATUS_OPTIONS = ["OPEN", "IN_PROGRESS", "RESOLVED", "REJECTED"];
const ADMIN_RESPONSE_OPTIONS = [
    "PENDING_REVIEW",
    "UNDER_INVESTIGATION",
    "RESOLVED_REFUNDED",
    "RESOLVED_REPLACED",
    "RESOLVED_COMPENSATED",
    "REJECTED_INVALID",
    "REJECTED_POLICY",
    "ESCALATED"
];

// ✅ FIX 1: Separate localStorage key so vendor responses don't overwrite user responses
const VENDOR_LOCAL_STORAGE_KEY = "vendorLocalAdminResponses";

const useInlineLoader = () => {
    const [loading, setLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);

    const showLoader = () => {
        setFadeOut(false);
        setLoading(true);
    };

    const hideLoader = () => {
        setFadeOut(true);
        setTimeout(() => {
            setLoading(false);
            setFadeOut(false);
        }, 500);
    };

    return { showLoader, hideLoader, loading, fadeOut };
};

const Loader = ({ loading, fadeOut, text = "Loading..." }) => {
    if (!loading) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            transition: 'opacity 0.4s ease',
            fontFamily: 'Arial, sans-serif',
            opacity: fadeOut ? 0 : 1,
            pointerEvents: fadeOut ? 'none' : 'auto'
        }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 900, letterSpacing: '6px', marginBottom: '30px' }}>
                    {"MAAMAAS".split("").map((l, i) => (
                        <span key={i} style={{
                            display: 'inline-block',
                            opacity: 0,
                            transform: 'translateY(30px)',
                            animation: `letterAnim 0.8s ease forwards ${i * 0.1}s`,
                            color: '#e50914',
                        }}>{l}</span>
                    ))}
                </div>
                <div style={{
                    width: '280px', height: '4px',
                    background: 'rgba(255,255,255,0.2)',
                    overflow: 'hidden', margin: '0 auto',
                }}>
                    <div style={{
                        width: '40%', height: '100%',
                        background: 'linear-gradient(90deg,#e50914,#ff6b6b)',
                        animation: 'progress 1.8s infinite',
                    }}></div>
                </div>
                <div style={{ marginTop: '20px', color: '#aaa', fontSize: '14px' }}>{text}</div>
            </div>
        </div>
    );
};

const Vendorcoustomercare = () => {
    const [selectedStatusFilter, setSelectedStatusFilter] = useState("");
    const [selectedTicketTypeFilter, setSelectedTicketTypeFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [vendorStats, setVendorStats] = useState({});
    const [currentIssueForView, setCurrentIssueForView] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [viewLoading, setViewLoading] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const [adminResponse, setAdminResponse] = useState("");
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [localAdminResponses, setLocalAdminResponses] = useState({});
    const [imagePreviewModal, setImagePreviewModal] = useState({ show: false, url: "" });

    const { showLoader, hideLoader, loading: globalLoading, fadeOut } = useInlineLoader();

    useEffect(() => {
        if (showViewModal || imagePreviewModal.show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [showViewModal, imagePreviewModal.show]);

    // ✅ FIX 2: Clean, single-endpoint status update — no broken retry logic
    const handleStatusChange = async (
        ticketId,
        statusToSet,
        responseMessage = ""
    ) => {
        try {
            const data =
                await updateTicketStatusAPI(
                    ticketId,
                    statusToSet,
                    responseMessage
                );

            return {
                success: true,
                data,
            };

        } catch (err) {
            return {
                success: false,
                error: err.message,
            };
        }
    };

    // ✅ FIX 1 applied: use vendor-specific localStorage key
    const storeAdminResponseLocally = (ticketId, response, notes = "") => {
        setLocalAdminResponses(prev => ({
            ...prev,
            [ticketId]: {
                response,
                notes,
                timestamp: new Date().toISOString(),
                adminName: localStorage.getItem("userName") || "Admin"
            }
        }));
    };

    // ✅ FIX 3: Resolve ID consistently — prefer ticketId, fallback to id
    const getCombinedAdminResponse = (ticket) => {
        const id = ticket.ticketId || ticket.id;
        const localResponse = localAdminResponses[id];
        if (localResponse) {
            return {
                response: localResponse.response,
                notes: localResponse.notes,
                isLocal: true,
                timestamp: localResponse.timestamp,
            };
        }
        return {
            response: ticket.adminResponse || ticket.responseMessage,
            notes: "",
            isLocal: false,
            timestamp: ticket.resolvedAt || ticket.createdAt
        };
    };

    useEffect(() => {
        fetchTickets();
        // ✅ FIX 1: Load from vendor-specific key
        const savedResponses = localStorage.getItem(VENDOR_LOCAL_STORAGE_KEY);
        if (savedResponses) {
            try { setLocalAdminResponses(JSON.parse(savedResponses)); } catch (_) { }
        }
    }, []);

    useEffect(() => {
        // ✅ FIX 1: Save to vendor-specific key
        localStorage.setItem(VENDOR_LOCAL_STORAGE_KEY, JSON.stringify(localAdminResponses));
    }, [localAdminResponses]);

    const calculateStats = (ticketList) => {
        const counts = { OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0, REJECTED: 0, Total: 0 };
        ticketList.forEach(ticket => {
            const status = ticket.status || "UNKNOWN";
            if (STATUS_OPTIONS.includes(status)) counts[status]++;
        });
        counts.Total = ticketList.length;
        setVendorStats(counts);
    };

    const fetchTickets = async () => {
        setLoading(true);

        setError("");

        showLoader();

        try {
            const vendorTickets =
                await getAllVendorTicketsAPI();

            console.log(
                "✅ Vendor Tickets:",
                vendorTickets
            );

            setTickets(
                vendorTickets
            );

            calculateStats(
                vendorTickets
            );

        } catch (err) {
            setError(
                err.message ||
                "Something went wrong"
            );

        } finally {
            setLoading(false);

            hideLoader();
        }
    };

    const handleShowViewModal =
        async (ticket) => {
            setShowViewModal(true);

            setViewLoading(true);

            setAdminResponse("");

            setNewStatus("");

            setAdditionalNotes("");

            const ticketId =
                ticket.ticketId ||
                ticket.id;

            console.log(
                "📌 Opening vendor ticket ID:",
                ticketId
            );

            try {
                const ticketData =
                    await getVendorTicketByIdAPI(
                        ticketId
                    );

                setCurrentIssueForView(
                    ticketData
                );

            } catch (error) {
                console.error(
                    "❌ Error fetching vendor ticket details:",
                    error
                );

                setCurrentIssueForView({
                    ...ticket,

                    id:
                        ticket.id ||
                        ticket.ticketId,

                    ticketId:
                        ticket.ticketId ||
                        ticket.id,

                    description:
                        ticket.description ||
                        ticket.message ||
                        "No description provided",
                });

            } finally {
                setViewLoading(false);
            }
        };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentIssueForView(null);
        setAdminResponse("");
        setNewStatus("");
        setAdditionalNotes("");
        setSaving(false);
    };

    const showImagePreview = (url) => setImagePreviewModal({ show: true, url });
    const closeImagePreview = () => setImagePreviewModal({ show: false, url: "" });

    // ✅ FIX 4: Search checks both message and description fields
    const filteredTickets = useMemo(() => {
        return tickets.filter(ticket => {
            const matchesStatus = !selectedStatusFilter || ticket.status === selectedStatusFilter;
            const type = ticket.issueType || ticket.ticketType;
            const matchesType = !selectedTicketTypeFilter || type === selectedTicketTypeFilter;
            const desc = ticket.description || ticket.message || "";
            const matchesSearch = !searchTerm ||
                desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (ticket.id && ticket.id.toString().includes(searchTerm)) ||
                (ticket.ticketId && ticket.ticketId.toString().includes(searchTerm)) ||
                (ticket.vendorId && ticket.vendorId.toString().includes(searchTerm));

            return matchesStatus && matchesType && matchesSearch;
        }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }, [tickets, selectedStatusFilter, selectedTicketTypeFilter, searchTerm]);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case "OPEN": return "bg-warning text-dark";
            case "IN_PROGRESS": return "bg-info text-dark";
            case "RESOLVED": return "bg-success text-white";
            case "REJECTED": return "bg-danger text-white";
            default: return "bg-secondary text-white";
        }
    };

    const getTicketTypeBadgeClass = (ticketType) => {
        switch (ticketType) {
            case "SERVICE_QUALITY": return "bg-primary text-white";
            case "FOOD_QUALITY": return "bg-success text-white";
            case "DELIVERY_ISSUE": return "bg-warning text-dark";
            case "PAYMENT_PROBLEM": return "bg-danger text-white";
            case "OTHER": return "bg-secondary text-white";
            default: return "bg-dark text-white";
        }
    };

    const getAdminResponseBadgeClass = (response) => {
        if (!response) return "bg-light text-dark";
        if (response.includes("RESOLVED")) return "bg-success text-white";
        if (response.includes("REJECTED")) return "bg-danger text-white";
        if (response.includes("PENDING") || response.includes("UNDER_INVESTIGATION")) return "bg-warning text-dark";
        if (response === "ESCALATED") return "bg-info text-dark";
        return "bg-secondary text-white";
    };

    const formatTicketType = (ticketType) =>
        ticketType ? ticketType.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Unknown";

    const formatAdminResponse = (response) => {
        if (!response) return "No Response";
        return response.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // ✅ FIX 5: Rewritten handleSaveChanges
    // - No alert() before close (alert blocks JS execution)
    // - Consistent ID resolution
    // - Optimistic update uses correct field names
    // - Background refresh after modal closes
    const handleSaveChanges = async () => {
        if (!newStatus && !adminResponse) {
            alert("Please select a new status or admin response before saving.");
            return;
        }

        setSaving(true);

        try {
            const ticketId = currentIssueForView.ticketId || currentIssueForView.id;
            const statusToSet = newStatus || currentIssueForView.status;

            console.log("💾 Saving vendor ticket — id:", ticketId, "status:", statusToSet, "response:", adminResponse);

            const result = await handleStatusChange(ticketId, statusToSet, adminResponse || "");

            if (!result.success) {
                alert("❌ Update failed: " + result.error);
                setSaving(false);
                return;
            }

            console.log("✅ Vendor ticket updated:", result.data);

            // Store admin response locally if provided
            if (adminResponse) {
                storeAdminResponseLocally(ticketId, adminResponse, additionalNotes);
            }

            // ✅ Optimistic UI update — reflect changes immediately in the table
            setTickets(prev =>
                prev.map(ticket => {
                    const id = ticket.ticketId || ticket.id;
                    if (id === ticketId) {
                        return {
                            ...ticket,
                            status: statusToSet,
                            adminResponse: adminResponse || ticket.adminResponse,
                            responseMessage: adminResponse || ticket.responseMessage,
                            resolvedAt: statusToSet === "RESOLVED" ? new Date().toISOString() : ticket.resolvedAt,
                        };
                    }
                    return ticket;
                })
            );

            // ✅ Recalculate stats after optimistic update
            setTickets(prev => {
                calculateStats(prev);
                return prev;
            });

            // ✅ Close modal first, then background refresh — no alert blocking
            handleCloseViewModal();

            // Background sync with server
            fetchTickets().catch(err => console.warn("Background refresh failed:", err));

            // Alert after close so it doesn't block modal animation
            setTimeout(() => alert("✅ Vendor ticket updated successfully!"), 100);

        } catch (error) {
            console.error("❌ Unexpected error:", error);
            alert("Something went wrong: " + error.message);
            setSaving(false);
        }
    };

    return (
        <div className="container-fluid mt-4 mb-5">
            <Loader loading={globalLoading} fadeOut={fadeOut} text="Loading vendor tickets data..." />

            <style>{`
                @keyframes letterAnim {
                    0% { opacity: 0; transform: translateY(30px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                @keyframes progress {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(250%); }
                }
            `}</style>

            {/* Stats Dashboard */}
            <div className="row row-cols-2 row-cols-sm-3 row-cols-md-3 row-cols-lg-4 row-cols-xl-5 justify-content-start text-center mb-4 gy-3 p-3">
                {STATUS_OPTIONS.map((status) => (
                    <div className="col" key={`stat-${status}`}>
                        <div className={`card h-100 text-white ${getStatusBadgeClass(status).split(" ")[0]}`}>
                            <div className="card-body p-2">
                                <h6 className="card-title mb-0">{vendorStats[status] || 0}</h6>
                                <small className="card-text">{status}</small>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="col">
                    <div className="card h-100 text-white" style={{ backgroundColor: "rgb(153, 0, 255)" }}>
                        <div className="card-body p-2">
                            <h6 className="card-title mb-0">{vendorStats.Total || 0}</h6>
                            <small className="card-text">Total Tickets</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tickets Table */}
            <div className="table-responsive shadow-sm rounded">
                <table className="table table-hover table-bordered align-middle caption-top">
                    <thead style={{ fontSize: "0.9rem", verticalAlign: "middle" }}>
                        <tr>
                            <th style={tableHeaderStyle} className="p-2">Ticket ID</th>
                            <th style={tableHeaderStyle} className="p-2">Vendor ID</th>
                            <th style={tableHeaderStyle} className="p-2">Type</th>
                            <th style={{ ...tableHeaderStyle, minWidth: "200px" }} className="p-2">Message</th>
                            <th style={tableHeaderStyle} className="p-2">Status</th>
                            <th style={tableHeaderStyle} className="p-2">Reported Date</th>
                            <th style={tableHeaderStyle} className="p-2">Reported Time</th>
                            <th style={tableHeaderStyle} className="p-2">Resolved At</th>
                            <th style={tableHeaderStyle} className="p-2">Admin Response</th>
                            <th style={tableHeaderStyle} className="p-2">Image</th>
                            <th style={{ ...tableHeaderStyle, minWidth: "130px" }} className="p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="11" className="text-center p-4">Loading tickets...</td></tr>
                        ) : error ? (
                            <tr><td colSpan="11" className="text-center p-4 text-danger">{error}</td></tr>
                        ) : filteredTickets.length > 0 ? (
                            filteredTickets.map((ticket) => {
                                const combinedResponse = getCombinedAdminResponse(ticket);
                                // ✅ Normalize display fields
                                const displayId = ticket.ticketId || ticket.id;
                                const displayType = ticket.issueType || ticket.ticketType;
                                const displayDesc = ticket.description || ticket.message || "No message";

                                return (
                                    <tr key={displayId} style={{ fontSize: "0.9rem" }}>
                                        <td style={tableCellStyle} className="p-2">{displayId}</td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.vendorId ? (
                                                <Link to={`/vendor-profile/${ticket.vendorId}`} className="text-decoration-none">
                                                    {ticket.vendorId}
                                                </Link>
                                            ) : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <span className={`badge rounded-pill ${getTicketTypeBadgeClass(displayType)}`}>
                                                {formatTicketType(displayType)}
                                            </span>
                                        </td>
                                        <td style={tableCellStyle} className="text-break p-2">{displayDesc}</td>
                                        <td style={tableCellStyle} className="p-2">
                                            <span className={`badge rounded-pill ${getStatusBadgeClass(ticket.status)}`}>
                                                {ticket.status || "UNKNOWN"}
                                            </span>
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.createdAt
                                                ? new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                : "N/A"}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.resolvedAt ? new Date(ticket.resolvedAt).toLocaleDateString() : 'N/A'}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <span className={`badge rounded-pill ${getAdminResponseBadgeClass(combinedResponse.response)}`}>
                                                {formatAdminResponse(combinedResponse.response)}
                                                {combinedResponse.isLocal && (
                                                    <span className="ms-1" title="Stored locally">
                                                        <i className="bi bi-device-ssd"></i>
                                                    </span>
                                                )}
                                            </span>
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            {ticket.attachmentUrl ? (
                                                <div style={{ width: "80px", height: "60px" }}>
                                                    <img
                                                        src={ticket.attachmentUrl}
                                                        alt="Ticket attachment"
                                                        className="img-thumbnail"
                                                        style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                                                        onClick={() => showImagePreview(ticket.attachmentUrl)}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://via.placeholder.com/80x60?text=No+Image';
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-muted">No Image</span>
                                            )}
                                        </td>
                                        <td style={tableCellStyle} className="p-2">
                                            <button
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => handleShowViewModal(ticket)}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="11" className="text-center p-4">
                                    <h5 className="text-muted">No tickets found.</h5>
                                    <p className="text-muted">
                                        {tickets.length === 0
                                            ? "No vendor support tickets have been created yet."
                                            : "No tickets match your filters."}
                                    </p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Image Preview Modal */}
            {imagePreviewModal.show && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{ zIndex: 10000, width: '100%', maxWidth: '1200px' }}>
                        <div className="modal-content bg-transparent border-0">
                            <div className="modal-header border-0">
                                <button type="button" className="btn-close btn-close-white" onClick={closeImagePreview}></button>
                            </div>
                            <div className="modal-body text-center">
                                <img
                                    src={imagePreviewModal.url}
                                    alt="Full size preview"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '80vh', maxWidth: '100%', boxShadow: '0 5px 20px rgba(0,0,0,0.3)' }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                                    }}
                                />
                                <div className="mt-4">
                                    <a href={imagePreviewModal.url} target="_blank" rel="noopener noreferrer"
                                        className="btn btn-primary me-3 px-4 py-2" style={{ borderRadius: '8px' }}>
                                        <i className="bi bi-box-arrow-up-right me-2"></i>Open in New Tab
                                    </a>
                                    <a href={imagePreviewModal.url} download
                                        className="btn btn-secondary px-4 py-2" style={{ borderRadius: '8px' }}>
                                        <i className="bi bi-download me-2"></i>Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View / Edit Modal */}
            {showViewModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9998,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{ zIndex: 9999, width: '100%', maxWidth: '600px', margin: '0 auto' }}>
                        <div className="modal-content" style={{
                            borderRadius: '10px',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
                            border: 'none',
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Header */}
                            <div className="modal-header text-white" style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderBottom: 'none',
                                padding: '1rem 1.2rem',
                                flexShrink: 0
                            }}>
                                <h5 className="modal-title fw-bold mb-0" style={{ fontSize: '1rem' }}>
                                    <i className="bi bi-ticket-detailed me-1"></i>
                                    {viewLoading
                                        ? "Loading Ticket..."
                                        : `Vendor Ticket #${currentIssueForView?.ticketId || currentIssueForView?.id || "N/A"}`}
                                </h5>
                                <button type="button" className="btn-close btn-close-white"
                                    onClick={handleCloseViewModal}
                                    style={{ filter: 'brightness(0) invert(1)', fontSize: '0.7rem' }}>
                                </button>
                            </div>

                            {/* Body */}
                            <div className="modal-body" style={{
                                padding: '1.2rem',
                                backgroundColor: '#f8f9fa',
                                overflowY: 'auto',
                                flex: 1
                            }}>
                                {viewLoading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }} role="status"></div>
                                        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>Fetching ticket details...</p>
                                    </div>
                                ) : currentIssueForView ? (
                                    <>
                                        {/* Info Grid */}
                                        <div className="row mb-3">
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Ticket ID</small>
                                                    <strong style={{ fontSize: '0.85rem' }}>
                                                        {currentIssueForView.ticketId || currentIssueForView.id}
                                                    </strong>
                                                </div>
                                            </div>
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Type</small>
                                                    {/* ✅ FIX 4: Check both issueType and ticketType */}
                                                    <span className={`badge ${getTicketTypeBadgeClass(currentIssueForView.issueType || currentIssueForView.ticketType)} px-2 py-1`} style={{ fontSize: '0.75rem' }}>
                                                        {formatTicketType(currentIssueForView.issueType || currentIssueForView.ticketType)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Vendor ID</small>
                                                    <strong style={{ fontSize: '0.85rem' }}>
                                                        {currentIssueForView.vendorId ? (
                                                            <Link to={`/vendor-profile/${currentIssueForView.vendorId}`} className="text-decoration-none text-primary">
                                                                {currentIssueForView.vendorId}
                                                            </Link>
                                                        ) : "N/A"}
                                                    </strong>
                                                </div>
                                            </div>
                                            <div className="col-6 mb-2">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Current Status</small>
                                                    <span className={`badge ${getStatusBadgeClass(currentIssueForView.status)} px-2 py-1`} style={{ fontSize: '0.75rem' }}>
                                                        {currentIssueForView.status || "UNKNOWN"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timestamps */}
                                        <div className="row mb-3">
                                            <div className="col-6">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Created</small>
                                                    <span style={{ fontSize: '0.8rem' }}>
                                                        {currentIssueForView.createdAt
                                                            ? new Date(currentIssueForView.createdAt).toLocaleString('en-GB')
                                                            : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-2 bg-light rounded border">
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Resolved</small>
                                                    <span style={{ fontSize: '0.8rem' }}>
                                                        {currentIssueForView.resolvedAt
                                                            ? new Date(currentIssueForView.resolvedAt).toLocaleString('en-GB')
                                                            : 'Not resolved'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description — ✅ FIX 4: show description OR message */}
                                        <div className="mb-3">
                                            <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                <i className="bi bi-chat-left-text me-1"></i>Issue Description
                                            </h6>
                                            <div className="p-3 bg-light rounded border">
                                                <p className="mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                                                    {currentIssueForView.description || currentIssueForView.message || "No description provided"}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Current Admin Response */}
                                        <div className="mb-3">
                                            <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                <i className="bi bi-person-badge me-1"></i>Current Response
                                            </h6>
                                            <div className="text-center">
                                                {(() => {
                                                    const id = currentIssueForView.ticketId || currentIssueForView.id;
                                                    const localResp = localAdminResponses[id];
                                                    const displayResp = localResp?.response
                                                        || currentIssueForView.adminResponse
                                                        || currentIssueForView.responseMessage;
                                                    return displayResp ? (
                                                        <span className={`badge ${getAdminResponseBadgeClass(displayResp)} px-3 py-2`} style={{ fontSize: '0.8rem' }}>
                                                            {formatAdminResponse(displayResp)}
                                                            {localResp && (
                                                                <span className="ms-1" title="Stored locally">
                                                                    <i className="bi bi-device-ssd"></i>
                                                                </span>
                                                            )}
                                                        </span>
                                                    ) : (
                                                        <div className="text-muted p-3 bg-light rounded">
                                                            <i className="bi bi-chat-square-text me-1"></i>No response yet
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        {/* Attachment */}
                                        {currentIssueForView.attachmentUrl && (
                                            <div className="mb-3">
                                                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="bi bi-image me-1"></i>Attachment
                                                </h6>
                                                <div className="text-center">
                                                    <img
                                                        src={currentIssueForView.attachmentUrl}
                                                        alt="Ticket attachment"
                                                        className="img-fluid rounded border mb-2"
                                                        style={{ maxHeight: '120px' }}
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = 'https://via.placeholder.com/200x120?text=Image';
                                                        }}
                                                    />
                                                    <div>
                                                        <a href={currentIssueForView.attachmentUrl} target="_blank" rel="noopener noreferrer"
                                                            className="btn btn-primary btn-sm me-1" style={{ fontSize: '0.8rem' }}>
                                                            <i className="bi bi-box-arrow-up-right me-1"></i>Open
                                                        </a>
                                                        <a href={currentIssueForView.attachmentUrl} download
                                                            className="btn btn-outline-secondary btn-sm" style={{ fontSize: '0.8rem' }}>
                                                            <i className="bi bi-download me-1"></i>Download
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Update Controls */}
                                        <div className="row mt-3">
                                            <div className="col-6">
                                                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="bi bi-arrow-clockwise me-1"></i>Update Status
                                                </h6>
                                                <select
                                                    className="form-select form-select-sm mb-2"
                                                    value={newStatus}
                                                    onChange={(e) => setNewStatus(e.target.value)}
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    <option value="">— Select status —</option>
                                                    {STATUS_OPTIONS.map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                                <div className="d-flex align-items-center gap-1">
                                                    <small className="text-muted" style={{ fontSize: '0.8rem' }}>Current:</small>
                                                    <span className={`badge ${getStatusBadgeClass(currentIssueForView.status)}`} style={{ fontSize: '0.75rem' }}>
                                                        {currentIssueForView.status}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <h6 className="fw-bold text-primary mb-2" style={{ fontSize: '0.9rem' }}>
                                                    <i className="bi bi-reply me-1"></i>Admin Response
                                                </h6>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={adminResponse}
                                                    onChange={(e) => setAdminResponse(e.target.value)}
                                                    style={{ fontSize: '0.85rem' }}
                                                >
                                                    <option value="">— Select response —</option>
                                                    {ADMIN_RESPONSE_OPTIONS.map(r => (
                                                        <option key={r} value={r}>{formatAdminResponse(r)}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="bi bi-exclamation-triangle text-danger mb-2" style={{ fontSize: '1.5rem' }}></i>
                                        <p className="text-danger mb-0" style={{ fontSize: '0.9rem' }}>Failed to load ticket details</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="modal-footer border-top-0 py-2 px-3" style={{
                                background: '#f8f9fa',
                                flexShrink: 0,
                                gap: "12px"
                            }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleCloseViewModal}
                                    disabled={saving}
                                    style={{ fontSize: '0.85rem' }}
                                >
                                    <i className="bi bi-x-lg me-1"></i>Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSaveChanges}
                                    disabled={(!newStatus && !adminResponse) || saving}
                                    style={{ fontSize: '0.85rem' }}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <><i className="bi bi-save me-1"></i>Save Changes</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const tableHeaderStyle = {
    border: '1px solid #ddd',
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: 'rgb(230 109 51)',
    color: 'white',
    minWidth: '95px'
};

const tableCellStyle = {
    border: '1px solid #ddd',
    textAlign: 'center',
    verticalAlign: 'middle'
};

export default Vendorcoustomercare;